import React, {useState} from "react";
import styles, {C} from "../styles";
import {generateAvatar, getAnonymousAvatar} from "../utils";
import AvatarBadge from "./AvatarBadge";
import {apiGetComments, apiCreateComment, apiDeleteComment} from "../api/comments";
import {formatDistanceToNow} from "date-fns";
import {fr} from "date-fns/locale";
import {useToast, useConfirm} from "./FeedbackProvider";

export default function IdeaComments({postId, initialCount, user}) {
    const {notify} = useToast();
    const confirm = useConfirm();
    const [expanded, setExpanded] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [comments, setComments] = useState([]);
    const [count, setCount] = useState(initialCount);
    const [content, setContent] = useState("");
    const [isAnonymous, setIsAnonymous] = useState(true);
    const [realName, setRealName] = useState("");
    const [submitting, setSubmitting] = useState(false);

    function reload() {
        return apiGetComments(postId).then(data => {
            const rows = Array.isArray(data) ? data : [];
            setComments(rows);
            setCount(rows.length);
            setLoaded(true);
        });
    }

    function toggle() {
        if (!expanded && !loaded) reload();
        setExpanded(!expanded);
    }

    function submitComment() {
        if (content.trim() === "" || (!isAnonymous && realName.trim() === "") || submitting) return;
        setSubmitting(true);
        apiCreateComment(postId, content, isAnonymous, realName).then(data => {
            if (data && data.error) { setSubmitting(false); notify(data.error, "error"); return; }
            reload().then(() => {
                setSubmitting(false);
                setContent("");
                setRealName("");
            });
        }).catch(() => {
            setSubmitting(false);
            notify("Impossible de publier le commentaire. Veuillez réessayer.", "error");
        });
    }

    async function removeComment(commentId) {
        if (!(await confirm("Supprimer ce commentaire ?", {danger: true}))) return;
        apiDeleteComment(commentId).then(reload);
    }

    const isSubmitDisabled = content.trim() === "" || (!isAnonymous && realName.trim() === "") || submitting;

    return (
        <div style={{marginTop: 8}}>
            <button onClick={toggle} style={{...styles.button, marginRight: 0, marginBottom: 0}}>
                💬 <span>{count === 0 ? "Commenter" : `${count} commentaire${count > 1 ? "s" : ""}`}</span>
            </button>

            {expanded && (
                <div style={{marginTop: 10, paddingLeft: 12, borderLeft: `2px solid ${C.border}`}}>
                    {comments.map(comment => {
                        const anonymous = comment.is_anonymous === 1;
                        const avatarData = anonymous ? getAnonymousAvatar(comment) : generateAvatar(comment.author);
                        return (
                            <div key={comment.id} style={{marginBottom: 10}}>
                                <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 4}}>
                                    <div style={{
                                        ...styles.avatarCircle,
                                        width: 26,
                                        height: 26,
                                        fontSize: 13,
                                        backgroundColor: avatarData.color,
                                    }}>
                                        <AvatarBadge avatar={avatarData}/>
                                    </div>
                                    <strong style={{fontSize: 12.5, color: C.text}}>{comment.author}</strong>
                                    {anonymous && (
                                        <span style={{
                                            fontSize: 10,
                                            fontWeight: 600,
                                            backgroundColor: "#F3F4F6",
                                            color: "#6B7280",
                                            border: "1px solid #D1D5DB",
                                            borderRadius: 20,
                                            padding: "1px 6px",
                                            letterSpacing: 0.3,
                                        }}>
                                            Anonyme
                                        </span>
                                    )}
                                    <span style={{fontSize: 11, color: C.textMuted}}>
                                        {formatDistanceToNow(new Date(comment.created_at), {addSuffix: true, locale: fr})}
                                    </span>
                                </div>
                                <p style={{margin: "0 0 4px", fontSize: 13, lineHeight: 1.5, color: C.text}}>
                                    {comment.content}
                                </p>
                                {user?.role === "admin" && (
                                    <button
                                        onClick={() => removeComment(comment.id)}
                                        style={{...styles.dangerButton, fontSize: 11, padding: "3px 8px"}}
                                    >
                                        Supprimer
                                    </button>
                                )}
                            </div>
                        );
                    })}

                    {user ? (
                        <div style={{marginTop: 8}}>
                            <div style={{
                                display: "flex",
                                gap: 0,
                                marginBottom: 8,
                                border: `1.5px solid ${C.border}`,
                                borderRadius: 8,
                                overflow: "hidden",
                                width: "fit-content",
                            }}>
                                <button
                                    onClick={() => setIsAnonymous(true)}
                                    style={{
                                        padding: "6px 12px",
                                        border: "none",
                                        cursor: "pointer",
                                        fontWeight: 600,
                                        fontSize: 12,
                                        backgroundColor: isAnonymous ? C.primary : "white",
                                        color: isAnonymous ? "white" : C.textMuted,
                                    }}
                                >
                                    🔒 Anonyme
                                </button>
                                <button
                                    onClick={() => setIsAnonymous(false)}
                                    style={{
                                        padding: "6px 12px",
                                        border: "none",
                                        borderLeft: `1.5px solid ${C.border}`,
                                        cursor: "pointer",
                                        fontWeight: 600,
                                        fontSize: 12,
                                        backgroundColor: !isAnonymous ? C.primary : "white",
                                        color: !isAnonymous ? "white" : C.textMuted,
                                    }}
                                >
                                    ✍️ En mon nom
                                </button>
                            </div>

                            {!isAnonymous && (
                                <input
                                    value={realName}
                                    onChange={e => setRealName(e.target.value)}
                                    placeholder="Votre prénom et nom"
                                    style={{...styles.input, marginBottom: 8, fontSize: 13}}
                                />
                            )}

                            <textarea
                                value={content}
                                onChange={e => setContent(e.target.value)}
                                placeholder="Ajouter un commentaire…"
                                style={{...styles.input, height: 60, marginBottom: 8, fontSize: 13}}
                            />
                            <button
                                onClick={submitComment}
                                disabled={isSubmitDisabled}
                                style={{
                                    ...styles.primaryButton,
                                    opacity: isSubmitDisabled ? 0.5 : 1,
                                    cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                                    fontSize: 13,
                                }}
                            >
                                {submitting ? "Envoi…" : "Publier"}
                            </button>
                        </div>
                    ) : (
                        <p style={{fontSize: 12, color: C.textMuted, fontStyle: "italic"}}>
                            Connectez-vous pour commenter.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
